import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@components/Button';
import { useAuth } from '@contexts/Auth';
import { getClientId } from '@contexts/Auth/OAuth2/utils';
import { useMerchant } from '@hooks/useMerchant';
import { useMerchantBankAccount } from '@hooks/useMerchantBankAccount';
import { PageWidget } from '@components/PageWidget';
import { Spinner } from '@components/Spinner';
import { getCurrencyByCountry } from '@/utils/currency';
import { Grid, Items, Item, Label, Value } from '../styled';
import { TabModeToggleContainer, SDKContainer } from './styled';
import { ReactComponent as EditIcon } from '@/assets/pencil.svg';
import { API_URL, REDIRECT_URL } from '@/constants/env';
import { Notice } from '@components/Notice';

function BankInfoTab() {
  const {
    data: bankData,
    isLoading: isBankDataLoading,
    refetch: refetchBankData,
    isRefetching: isBankDataRefetching,
  } = useMerchantBankAccount();
  const { data: merchantData, isLoading: isMerchantLoading } = useMerchant();
  const { testMode } = useAuth();
  const hasBankInfo = useMemo(
    () =>
      typeof bankData?.last4 === 'string' &&
      typeof bankData?.bank_name === 'string',
    [bankData]
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const sdkRef = useRef(null);
  const KYCComponent = useMemo(
    () =>
      new (window as any).ContributeTo.KYC({
        env: API_URL?.includes('sbx')
          ? 'sbx'
          : API_URL?.includes('stg')
          ? 'stg'
          : 'prod',
      }),
    []
  );

  useEffect(() => {
    !hasBankInfo && setIsEditMode(true);
  }, [hasBankInfo]);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    sdkRef.current &&
      // @ts-ignore
      (sdkRef.current as HTMLElement).innerHTML !== '' &&
      KYCComponent.destroy();

    KYCComponent.render('#cto-sdk-kyc', {
      clientId: getClientId(testMode),
      redirectUri: REDIRECT_URL,
      onKYCSuccess: () => {
        setIsEditMode(false);
        refetchBankData();
      },
    });
  }, [isEditMode, isBankDataLoading, testMode, KYCComponent, refetchBankData]);

  if (isBankDataLoading || isMerchantLoading || isBankDataRefetching) {
    return (
      <PageWidget fullWidth={true}>
        <Spinner variant='small' />
      </PageWidget>
    );
  }

  return (
    <PageWidget fullWidth={false}>
      {!isEditMode && hasBankInfo && (
        <TabModeToggleContainer>
          <Button onClick={() => setIsEditMode(true)} variant='icon'>
            <EditIcon />
          </Button>
        </TabModeToggleContainer>
      )}
      {isEditMode ? (
        <SDKContainer>
          {!hasBankInfo && (
            <Notice title='Connect bank account'>
              <p>Connect your bank account to withdraw your funds.</p>
            </Notice>
          )}
          <div id='cto-sdk-kyc' ref={sdkRef}></div>
          {hasBankInfo && (
            <Button onClick={() => setIsEditMode(false)} variant='text'>
              Cancel
            </Button>
          )}
        </SDKContainer>
      ) : (
        <Grid>
          {bankData && (
            <>
              <Items>
                <Item>
                  <Label>Bank</Label>
                  <Value>{bankData.bank_name}</Value>
                </Item>
                <Item>
                  <Label>Country</Label>
                  <Value>{merchantData?.country}</Value>
                </Item>
              </Items>
              <Items>
                <Item>
                  <Label>
                    {merchantData?.country === 'US' ? 'Account' : 'IBAN'}
                  </Label>
                  <Value>*********{bankData.last4}</Value>
                </Item>
                <Item>
                  <Label>Currency</Label>
                  <Value>
                    {merchantData?.country &&
                      getCurrencyByCountry(merchantData.country)}
                  </Value>
                </Item>
              </Items>
            </>
          )}
        </Grid>
      )}
    </PageWidget>
  );
}

export { BankInfoTab };
